import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { LevelData, AdditionFact } from '../data/curriculum';
import { useTTS } from '../hooks/useTTS';
import { factToSpeechText, factToMissingPartQuestionSpeechText } from '../data/audioTextGenerator';
import FactDisplay, { MissingPart } from '../components/FactDisplay';
import AnswerInput from '../components/AnswerInput';
import TTSPlaybackIndicator from '../components/TTSPlaybackIndicator';
import ReviewResults from '../components/ReviewResults';
import { motion, AnimatePresence } from 'framer-motion';
import { playSuccessSound } from '../utils/sounds';

interface Step7MixedReviewProps {
  level: LevelData;
  onComplete: (passed: boolean, attempts: number, accuracy: number) => void;
  onRetry: () => void;
}

function factKey(fact: AdditionFact): string {
  return `${fact.addend1}_${fact.addend2}`;
}

function pickRandomFact(facts: AdditionFact[], scores: Map<string, number>): AdditionFact | null {
  const eligible = facts.filter((f) => (scores.get(factKey(f)) ?? 0) < 1);
  if (eligible.length === 0) return null;
  return eligible[Math.floor(Math.random() * eligible.length)];
}

function pickNextQuestion(
  facts: AdditionFact[],
  scores: Map<string, number>
): { fact: AdditionFact; missingPart: MissingPart } | null {
  // Priority 1: incomplete sum questions
  const eligibleSums = facts.filter((f) => (scores.get(factKey(f)) ?? 0) < 1);
  if (eligibleSums.length > 0) {
    return {
      fact: eligibleSums[Math.floor(Math.random() * eligibleSums.length)],
      missingPart: 'sum',
    };
  }
  // Priority 2: incomplete missing-addend questions
  const eligibleAddends: { fact: AdditionFact; missingPart: 'addend1' | 'addend2' }[] = [];
  for (const fact of facts) {
    for (const missing of ['addend1', 'addend2'] as const) {
      if ((scores.get(`${factKey(fact)}_${missing}`) ?? 0) < 1) {
        eligibleAddends.push({ fact, missingPart: missing });
      }
    }
  }
  if (eligibleAddends.length > 0) {
    return eligibleAddends[Math.floor(Math.random() * eligibleAddends.length)];
  }
  return null;
}

function questionKey(fact: AdditionFact, missing: MissingPart): string {
  return missing === 'sum' ? factKey(fact) : `${factKey(fact)}_${missing}`;
}

function getCorrectAnswer(fact: AdditionFact, missing: MissingPart): number {
  if (missing === 'sum') return fact.sum;
  if (missing === 'addend1') return fact.addend1;
  return fact.addend2;
}

const Step7MixedReview: React.FC<Step7MixedReviewProps> = ({
  level,
  onComplete,
  onRetry,
}) => {
  // Combine primary and commutative facts
  const allFacts = useMemo(
    () => [...level.primaryFacts, ...level.commutativeFacts],
    [level.primaryFacts, level.commutativeFacts]
  );

  // Score-based queue: each fact starts at 0, needs score >= 1 to be "done"
  const [factScores, setFactScores] = useState<Map<string, number>>(() => {
    const map = new Map<string, number>();
    allFacts.forEach((f) => {
      map.set(factKey(f), 0);
      map.set(`${factKey(f)}_addend1`, 0);
      map.set(`${factKey(f)}_addend2`, 0);
    });
    return map;
  });

  const [totalAttempts, setTotalAttempts] = useState(0);
  const [totalCorrect, setTotalCorrect] = useState(0);
  const [feedback, setFeedback] = useState<'correct' | 'incorrect' | null>(null);
  const [isAdvancing, setIsAdvancing] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const { playText, isLoading, isPlaying, error: ttsError } = useTTS();
  const advanceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const feedbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Store the current fact in a ref so it stays stable across re-renders.
  // Use state to trigger re-renders when we pick a new fact.
  const [currentFact, setCurrentFact] = useState<AdditionFact | null>(() =>
    pickRandomFact(allFacts, new Map(allFacts.map((f) => [factKey(f), 0])))
  );

  // Track which part is missing for the current question
  const [missingPart, setMissingPart] = useState<MissingPart>('sum');

  // Track the fact the student must retry (on incorrect answer)
  const [retryFact, setRetryFact] = useState<AdditionFact | null>(null);

  // The fact to display is the retry fact if we're in retry mode, otherwise the current fact
  const displayedFact = retryFact || currentFact;

  // Play question audio when moving to a new fact (skip in retry mode — student just heard the answer)
  useEffect(() => {
    if (displayedFact && !showResults && !retryFact) {
      playText(factToMissingPartQuestionSpeechText(displayedFact, missingPart));
    }
  }, [displayedFact, missingPart]); // eslint-disable-line react-hooks/exhaustive-deps

  // Check if all facts are done
  const allDone = useMemo(() => {
    return Array.from(factScores.values()).every((score) => score >= 1);
  }, [factScores]);

  // When all facts are done, show results
  useEffect(() => {
    if (allDone && !showResults && !retryFact) {
      setShowResults(true);
    }
  }, [allDone, showResults, retryFact]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current);
      if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);
    };
  }, []);

  const handleSubmit = useCallback(
    (answer: number) => {
      if (isAdvancing || !displayedFact) return;
      const key = questionKey(displayedFact, missingPart);

      setTotalAttempts((prev) => prev + 1);

      if (answer === getCorrectAnswer(displayedFact, missingPart)) {
        playSuccessSound();
        setFeedback('correct');
        setIsAdvancing(true);

        if (!retryFact) {
          // Only count towards accuracy and score on non-retry attempts
          setTotalCorrect((prev) => prev + 1);
          setFactScores((prev) => {
            const newMap = new Map(prev);
            const currentScore = Math.max(newMap.get(key) ?? 0, 0);
            newMap.set(key, currentScore + 1);
            return newMap;
          });
        }

        advanceTimerRef.current = setTimeout(() => {
          setFeedback(null);
          setIsAdvancing(false);
          setRetryFact(null);

          setFactScores((latestScores) => {
            const next = pickNextQuestion(allFacts, latestScores);
            if (next) {
              setCurrentFact(next.fact);
              setMissingPart(next.missingPart);
            }
            return latestScores;
          });
        }, 600);
      } else {
        setFeedback('incorrect');
        setIsAdvancing(true);
        playText(factToSpeechText(displayedFact));

        if (!retryFact) {
          // First incorrect: decrement score (floor at 0, so incorrect just keeps it at 0)
          setFactScores((prev) => {
            const newMap = new Map(prev);
            const currentScore = newMap.get(key) ?? 0;
            newMap.set(key, Math.max(currentScore - 1, 0));
            return newMap;
          });
        }

        // Show correct answer briefly then require retry of THIS specific fact
        feedbackTimerRef.current = setTimeout(() => {
          setFeedback(null);
          setIsAdvancing(false);
          setRetryFact(displayedFact);
        }, 1500);
      }
    },
    [displayedFact, isAdvancing, retryFact, allFacts, missingPart]
  );

  const accuracy = totalAttempts > 0 ? (totalCorrect / totalAttempts) * 100 : 0;
  const passed = accuracy >= 90;

  if (showResults) {
    return (
      <ReviewResults
        passed={passed}
        isLastLevel={level.level === 11}
        onNextLevel={() => onComplete(passed, totalAttempts, accuracy)}
        onRetry={onRetry}
      />
    );
  }

  if (!displayedFact) {
    // Should not happen, but safety fallback
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white p-8 rounded-2xl shadow-lg text-center max-w-md w-full"
    >

      <div className="mb-6">
        {retryFact ? (
          <div>
            <FactDisplay fact={displayedFact} showAnswer={true} size="large" missingPart={missingPart} />
          </div>
        ) : (
          <FactDisplay fact={displayedFact} showAnswer={false} size="large" missingPart={missingPart} />
        )}
      </div>

      <TTSPlaybackIndicator isLoading={isLoading} isPlaying={isPlaying} error={ttsError} />

      <AnimatePresence mode="wait">
        {feedback === 'correct' && (
          <motion.div
            key="correct"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            className="text-green-500 font-bold text-lg mb-4"
          >
            Correct!
          </motion.div>
        )}
        {feedback === 'incorrect' && (
          <motion.div
            key="incorrect"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            className="text-red-500 font-bold text-lg mb-4"
          >
            The answer is {getCorrectAnswer(displayedFact, missingPart)}
          </motion.div>
        )}
        {feedback === null && <div className="h-[28px] mb-4" />}
      </AnimatePresence>

      <AnswerInput
        answer={getCorrectAnswer(displayedFact, missingPart)}
        onSubmit={handleSubmit}
        disabled={isAdvancing}
        feedback={feedback}
        resetKey={retryFact ? `retry_${questionKey(displayedFact, missingPart)}` : questionKey(displayedFact, missingPart)}
      />
    </motion.div>
  );
};

export default Step7MixedReview;
